import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { COMPANY_REGULARIZATION_SETTINGS } from "@/constants/apiEndpoints";
import { attendanceRegularizationApi } from "@/services/(my-space)/attendanceRegularizationApi";
import type {
  TGetRegularizationSettingsRequest,
  TGetRegularizationSettingsResponse,
  TResetRegularizationSettingsRequest,
  TResetRegularizationSettingsResponse,
  TUpdateRegularizationSettingsRequest,
  TUpdateRegularizationSettingsResponse,
} from "@/types/(company)/regularizationSettings.interface";

/**
 * Saving the company policy changes what the *employee* request form is
 * allowed to offer, and that lives in a different RTK slice
 * (`invalidatesTags` can't cross slices). An admin who is also an employee
 * would otherwise keep a stale date-picker bound until a refresh.
 */
const invalidateEmployeePolicy = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  },
) => {
  try {
    await queryFulfilled;
    dispatch(
      attendanceRegularizationApi.util.invalidateTags([
        "getRegularizationPolicy",
      ]),
    );
  } catch {
    // request failed — nothing to invalidate
  }
};

export const companyRegularizationSettingsApi = createApi({
  reducerPath: "companyRegularizationSettingsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getRegularizationSettings"],
  endpoints: (builder) => ({
    getRegularizationSettings: builder.query<
      TGetRegularizationSettingsResponse,
      TGetRegularizationSettingsRequest
    >({
      query: () => ({
        url: COMPANY_REGULARIZATION_SETTINGS,
        method: "GET",
      }),
      providesTags: ["getRegularizationSettings"],
    }),
    updateRegularizationSettings: builder.mutation<
      TUpdateRegularizationSettingsResponse,
      TUpdateRegularizationSettingsRequest
    >({
      query: (body) => ({
        url: COMPANY_REGULARIZATION_SETTINGS,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getRegularizationSettings"],
      onQueryStarted: invalidateEmployeePolicy,
    }),
    resetRegularizationSettings: builder.mutation<
      TResetRegularizationSettingsResponse,
      TResetRegularizationSettingsRequest
    >({
      query: () => ({
        url: `${COMPANY_REGULARIZATION_SETTINGS}/reset`,
        method: "POST",
      }),
      invalidatesTags: ["getRegularizationSettings"],
      onQueryStarted: invalidateEmployeePolicy,
    }),
  }),
});

export const {
  useGetRegularizationSettingsQuery,
  useUpdateRegularizationSettingsMutation,
  useResetRegularizationSettingsMutation,
} = companyRegularizationSettingsApi;
